import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI } from '../api';
import { ChevronLeft, Package, Calendar, Zap, Clock, Leaf, ChevronRight, ShoppingBag, RefreshCw } from 'lucide-react';

const MyOrders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('active');

  const loadOrders = useCallback(async () => {
    try {
      const data = await ordersAPI.list();
      setOrders(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (user === false) { navigate('/auth'); return; }
    if (user) loadOrders();
  }, [user, navigate, loadOrders]);

  const active = orders.filter(o => !['completed', 'delivered', 'cancelled'].includes(o.status));
  const past = orders.filter(o => ['completed', 'delivered', 'cancelled'].includes(o.status));
  const filtered = tab === 'active' ? active : past;

  const getStatusInfo = (s) => {
    const map = {
      placed: { label: 'Placed', color: '#D4AF37', bg: 'rgba(212,175,55,0.1)' },
      assigned: { label: 'Tailor Assigned', color: '#D4AF37', bg: 'rgba(212,175,55,0.1)' },
      stitching: { label: 'Stitching', color: '#E5B2B9', bg: 'rgba(229,178,185,0.2)' },
      'counter-offered': { label: 'Date Change', color: '#D4AF37', bg: 'rgba(212,175,55,0.1)' },
      ready: { label: 'Ready', color: '#166534', bg: 'rgba(22,101,52,0.1)' },
      shipped: { label: 'Shipped', color: '#166534', bg: 'rgba(22,101,52,0.1)' },
      completed: { label: 'Delivered', color: '#166534', bg: 'rgba(22,101,52,0.1)' },
      delivered: { label: 'Delivered', color: '#166534', bg: 'rgba(22,101,52,0.1)' },
      cancelled: { label: 'Cancelled', color: '#991B1B', bg: 'rgba(153,27,27,0.08)' },
    };
    return map[s] || { label: s, color: '#595959', bg: '#F5F5F5' };
  };

  const getDlIcon = (t) => t === 'urgent' ? Zap : t === 'flexible' ? Leaf : Clock;

  if (user === null || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div data-testid="my-orders" className="min-h-screen" style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>
      <div className="sticky top-0 z-50 flex items-center justify-between px-3 py-3 border-b bg-white" style={{ borderColor: '#E5E5E5' }}>
        <button data-testid="back-btn" onClick={() => navigate('/landing')} className="p-1 -ml-1">
          <ChevronLeft className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
        </button>
        <h1 className="text-base" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>My Orders</h1>
        <button onClick={loadOrders} className="p-1"><RefreshCw className="w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} /></button>
      </div>

      {/* Tabs */}
      <div className="px-3 pt-3 flex gap-2">
        {['active', 'past'].map(t => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className="flex-1 py-2 text-[11px] tracking-[0.05em] uppercase text-center transition-colors"
            style={{ backgroundColor: tab === t ? '#0F0F0F' : '#FFF', color: tab === t ? '#FFF' : '#595959', border: `1px solid ${tab === t ? '#0F0F0F' : '#E5E5E5'}` }}>
            {t === 'active' ? `Active (${active.length})` : `Past (${past.length})`}
          </button>
        ))}
      </div>

      <div className="px-3 py-3">
        {filtered.length > 0 ? (
          <div className="space-y-2">
            {filtered.map(order => {
              const si = getStatusInfo(order.status);
              const DlIcon = getDlIcon(order.delivery_type);
              return (
                <button key={order.id} data-testid={`order-${order.order_id}`}
                  onClick={() => navigate('/order-tracking', { state: { orderId: order.order_id, ...order } })}
                  className="w-full bg-white border p-3 text-left transition-colors" style={{ borderColor: '#E5E5E5' }}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <p className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{order.product}</p>
                        <span className="px-1.5 py-px text-[9px]" style={{ backgroundColor: si.bg, color: si.color }}>{si.label}</span>
                      </div>
                      <p className="text-[11px]" style={{ color: '#595959' }}>{order.order_id}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{'\u20B9'}{order.total_price?.toLocaleString('en-IN')}</span>
                      <ChevronRight className="w-4 h-4" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[11px]">
                    <span className="flex items-center gap-1" style={{ color: '#595959' }}>
                      <Calendar className="w-3 h-3" strokeWidth={1.5} />
                      {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'TBD'}
                    </span>
                    <span className="flex items-center gap-1" style={{ color: '#595959' }}>
                      <DlIcon className="w-3 h-3" strokeWidth={1.5} />
                      {order.delivery_type === 'urgent' ? 'Urgent' : order.delivery_type === 'flexible' ? 'Flexible' : 'Standard'}
                    </span>
                    {order.tailor_name && (
                      <span className="text-[10px]" style={{ color: '#595959' }}>by {order.tailor_name}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="py-16 text-center">
            <ShoppingBag className="w-10 h-10 mx-auto mb-3" style={{ color: '#E5E5E5' }} strokeWidth={1.5} />
            <p className="text-sm mb-1" style={{ color: '#595959' }}>No {tab} orders</p>
            <button onClick={() => navigate('/gender-selection')} className="mt-3 px-6 py-2 text-[11px] tracking-[0.08em] uppercase text-white"
              style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
              Start Shopping
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
